import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, User, Calendar, FileText, Send, Loader2,
  AlertTriangle, CheckCircle2, Sparkles, Paperclip, MessageSquare,
  ChevronRight, Building2, Target, Mail, DollarSign, Link2,
  Play, Pause, XCircle, RotateCcw, Upload, Trash2, FolderKanban
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

// Status configuration - matches backend config
const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700' },
  on_hold: { label: 'On Hold', color: 'bg-orange-100 text-orange-700' },
  waiting_on_requestor: { label: 'Waiting on Requestor', color: 'bg-purple-100 text-purple-700' },
  in_review: { label: 'In Review', color: 'bg-cyan-100 text-cyan-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700' }
};

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent (72 hrs)', color: 'bg-red-100 text-red-700' },
  high: { label: 'High (5 days)', color: 'bg-orange-100 text-orange-700' },
  standard: { label: 'Standard (7 days)', color: 'bg-blue-100 text-blue-700' },
  low: { label: 'Low (10 days)', color: 'bg-slate-100 text-slate-700' }
};

// Status transition buttons based on current status - matches backend VALID_STATUS_TRANSITIONS
const STATUS_ACTIONS = {
  submitted: [
    { status: 'in_progress', label: 'Start Work', icon: Play },
    { status: 'blocked', label: 'Mark Blocked', icon: Pause, variant: 'outline' },
    { status: 'on_hold', label: 'Put On Hold', icon: Pause, variant: 'outline' },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  in_progress: [
    { status: 'in_review', label: 'Send for Review', icon: FileText },
    { status: 'waiting_on_requestor', label: 'Waiting on Requestor', icon: Pause, variant: 'outline' },
    { status: 'blocked', label: 'Mark Blocked', icon: Pause, variant: 'outline' },
    { status: 'completed', label: 'Mark Complete', icon: CheckCircle2 },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  blocked: [
    { status: 'in_progress', label: 'Resume Work', icon: Play },
    { status: 'on_hold', label: 'Put On Hold', icon: Pause, variant: 'outline' },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  on_hold: [
    { status: 'in_progress', label: 'Resume Work', icon: Play },
    { status: 'blocked', label: 'Mark Blocked', icon: Pause, variant: 'outline' },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  waiting_on_requestor: [
    { status: 'in_progress', label: 'Resume Work', icon: Play },
    { status: 'blocked', label: 'Mark Blocked', icon: Pause, variant: 'outline' },
    { status: 'on_hold', label: 'Put On Hold', icon: Pause, variant: 'outline' },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  in_review: [
    { status: 'in_progress', label: 'Needs Changes', icon: RotateCcw, variant: 'outline' },
    { status: 'waiting_on_requestor', label: 'Waiting on Requestor', icon: Pause, variant: 'outline' },
    { status: 'completed', label: 'Mark Complete', icon: CheckCircle2 },
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'destructive' }
  ],
  completed: [
    { status: 'closed', label: 'Close Request', icon: XCircle, variant: 'outline' }
  ],
  closed: [
    { status: 'submitted', label: 'Reopen Request', icon: RotateCcw, variant: 'outline' }
  ]
};

const RequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [request, setRequest] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef(null);

  const canManage = ['admin', 'manager', 'contributor'].includes(user?.role);
  const isRequester = request?.requester_id === user?.id;

  const fetchData = useCallback(async () => {
    try {
      const [requestRes, commentsRes, usersRes, projectsRes] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/comments`),
        api.get('/users'),
        api.get('/projects')
      ]);
      setRequest(requestRes.data);
      setComments(commentsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch request:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/requests/${id}`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (assigneeId) => {
    setUpdating(true);
    try {
      await api.put(`/requests/${id}`, { primary_assignee: assigneeId });
      fetchData();
    } catch (error) {
      console.error('Failed to assign:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async (priority) => {
    setUpdating(true);
    try {
      await api.put(`/requests/${id}`, { priority });
      fetchData();
    } catch (error) {
      console.error('Failed to update priority:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    setUpdating(true);
    try {
      await api.put(`/requests/${id}`, { related_project_id: projectId || null });
      fetchData();
      toast.success(projectId ? 'Request linked to project' : 'Project link removed');
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project link');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      await api.post(`/requests/${id}/comments`, {
        content: newComment,
        is_internal: isInternalComment && canManage
      });
      setNewComment('');
      setIsInternalComment(false);
      fetchData();
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);

    try {
      // Step 1: Upload to object storage
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { file_id, file_url, file_type, file_size } = uploadRes.data;
      const file_name = uploadRes.data.file_name || uploadRes.data.filename;
      
      // Step 2: Attach to request
      await api.post(`/requests/${id}/attachments`, null, {
        params: { file_id, file_name, file_url, file_type, file_size }
      });
      
      toast.success('File attached successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = async (fileId) => {
    try {
      await api.delete(`/requests/${id}/attachments/${fileId}`);
      toast.success('Attachment removed');
      fetchData();
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      toast.error('Failed to remove attachment');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/requests/${id}`);
      toast.success('Request deleted successfully');
      navigate('/requests');
    } catch (error) {
      console.error('Failed to delete request:', error);
      toast.error('Failed to delete request');
    }
  };

  const handleCreateTask = async () => {
    try {
      const res = await api.post(`/requests/${id}/link-task`);
      alert(`Task created: ${res.data.task_id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900">Request not found</h2>
        <Button onClick={() => navigate('/requests')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Requests
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.submitted;
  const priorityConfig = PRIORITY_CONFIG[request.priority] || PRIORITY_CONFIG.standard;
  const statusActions = STATUS_ACTIONS[request.status] || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="request-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/requests')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{request.title}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
            <Badge variant="outline">{request.request_type}</Badge>
            {request.is_sla_breached && (
              <Badge className="bg-red-100 text-red-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                SLA Breached
              </Badge>
            )}
          </div>
        </div>
        {/* Delete Button - Admin/Manager only */}
        {canManage && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Request</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this request? This action cannot be undone.
                  All associated comments and attachments will also be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                  Delete Request
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="mcc-card">
            <CardHeader>
              <CardTitle className="text-base">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-500">Description</Label>
                <p className="mt-1 text-slate-900 whitespace-pre-wrap">{request.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Business Unit</p>
                    <p className="text-sm font-medium">{request.business_unit}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Target Audience</p>
                    <p className="text-sm font-medium">{request.target_audience || 'Not specified'}</p>
                  </div>
                </div>

                {request.distribution_channels?.length > 0 && (
                  <div className="flex items-start gap-2 md:col-span-2">
                    <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Distribution Channels</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {request.distribution_channels.map(ch => (
                          <Badge key={ch} variant="outline" className="text-xs">{ch}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {request.campaign_initiative && (
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Campaign</p>
                      <p className="text-sm font-medium">{request.campaign_initiative}</p>
                    </div>
                  </div>
                )}

                {request.budget_required && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Budget</p>
                      <p className="text-sm font-medium">
                        {request.estimated_budget ? `$${request.estimated_budget.toLocaleString()}` : 'TBD'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Triage Insights */}
          {request.ai_triage && (
            <Card className="mcc-card border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-purple-700">
                  <Sparkles className="w-4 h-4" />
                  AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Suggested Priority</p>
                    <Badge className={PRIORITY_CONFIG[request.ai_triage.suggested_priority]?.color}>
                      {PRIORITY_CONFIG[request.ai_triage.suggested_priority]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Suggested Assignee</p>
                    <p className="text-sm font-medium">{request.ai_triage.suggested_assignee_name || 'None'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Est. Hours</p>
                    <p className="text-sm font-medium">{request.ai_triage.estimated_hours}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Clarity Score</p>
                    <p className="text-sm font-medium">{Math.round(request.ai_triage.clarity_score * 100)}%</p>
                  </div>
                </div>

                {request.ai_triage.missing_info?.length > 0 && (
                  <div className="pt-3 border-t border-purple-200">
                    <p className="text-xs text-slate-500 mb-2">Missing Information</p>
                    <ul className="list-disc list-inside text-sm text-purple-700 space-y-1">
                      {request.ai_triage.missing_info.slice(0, 5).map((info, i) => (
                        <li key={i}>{info}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({request.attachments?.length || 0})
                </CardTitle>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={handleFileUpload} />
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </span>
                  </Button>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {request.attachments?.length > 0 ? (
                <div className="space-y-2">
                  {request.attachments.map(att => (
                    <a
                      key={att.id}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/requests/${request.id}/attachments/${att.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{att.filename}</p>
                        <p className="text-xs text-slate-500">
                          {(att.file_size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No attachments yet</p>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleAddComment} className="space-y-3">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  data-testid="comment-input"
                />
                <div className="flex items-center justify-between">
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal"
                        checked={isInternalComment}
                        onCheckedChange={setIsInternalComment}
                      />
                      <Label htmlFor="internal" className="text-sm cursor-pointer">
                        Internal note (hidden from requester)
                      </Label>
                    </div>
                  )}
                  <Button type="submit" size="sm" disabled={submittingComment || !newComment.trim()}>
                    {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Post
                  </Button>
                </div>
              </form>

              <Separator />

              <ScrollArea className="max-h-96">
                <div className="space-y-4">
                  {comments.map(comment => (
                    <div
                      key={comment.id}
                      className={`p-3 rounded-lg ${comment.is_internal ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{comment.author_name}</span>
                          {comment.is_internal && (
                            <Badge variant="outline" className="text-xs text-amber-700">Internal</Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">
                          {formatDistanceToNow(parseISO(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.content}</p>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No comments yet</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="mcc-card" data-testid="attachments-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({(request.attachments || []).length})
                </CardTitle>
                <div>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.pptx,.txt,.csv,.xlsx"
                    data-testid="attachment-file-input"
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    data-testid="add-attachment-btn"
                  >
                    {uploadingAttachment ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    {uploadingAttachment ? 'Uploading...' : 'Add File'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(request.attachments || []).length > 0 ? (
                <div className="space-y-2">
                  {request.attachments.map((att) => {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    return (
                      <div key={att.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg group" data-testid={`attachment-${att.id}`}>
                        <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{att.file_name}</p>
                          <p className="text-xs text-slate-500">
                            {att.uploaded_by_name} • {att.uploaded_at ? formatDistanceToNow(parseISO(att.uploaded_at), { addSuffix: true }) : ''}
                          </p>
                        </div>
                        <a
                          href={`${backendUrl}${att.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View
                        </a>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-7 h-7 opacity-0 group-hover:opacity-100"
                            onClick={() => handleRemoveAttachment(att.id)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No attachments yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {canManage && statusActions.length > 0 && (
            <Card className="mcc-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {statusActions.map(action => {
                  const ActionIcon = action.icon;
                  return (
                    <Button
                      key={action.status}
                      variant={action.variant || 'default'}
                      className="w-full justify-start"
                      onClick={() => handleStatusChange(action.status)}
                      disabled={updating}
                    >
                      <ActionIcon className="w-4 h-4 mr-2" />
                      {action.label}
                    </Button>
                  );
                })}
                
                {!request.linked_task_id && (
                  <Button
                    variant="outline"
                    className="w-full justify-start mt-4"
                    onClick={handleCreateTask}
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assignment */}
          {canManage && (
            <Card className="mcc-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                <Select 
                  value={request.assigned_to || 'unassigned'} 
                  onValueChange={(v) => handleAssign(v === 'unassigned' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.filter(u => ['admin', 'manager', 'contributor'].includes(u.role)).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {request.ai_triage?.suggested_assignee_id && request.assigned_to !== request.ai_triage.suggested_assignee_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-purple-600"
                    onClick={() => handleAssign(request.ai_triage.suggested_assignee_id)}
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Use AI suggestion: {request.ai_triage.suggested_assignee_name}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Priority */}
          {canManage && (
            <Card className="mcc-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Priority</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={request.priority} onValueChange={handlePriorityChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {/* Linked Project */}
          <Card className="mcc-card" data-testid="linked-project-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FolderKanban className="w-4 h-4" />
                Project
              </CardTitle>
            </CardHeader>
            <CardContent>
              {canManage ? (
                <Select 
                  value={request.related_project_id || 'none'} 
                  onValueChange={(v) => handleProjectChange(v === 'none' ? null : v)}
                >
                  <SelectTrigger data-testid="request-project-link-select">
                    <SelectValue placeholder="Link to project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-700">
                  {projects.find(p => p.id === request.related_project_id)?.name || 'Not linked'}
                </p>
              )}
              {request.related_project_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-blue-600"
                  onClick={() => navigate(`/projects/${request.related_project_id}`)}
                  data-testid="view-linked-project-btn"
                >
                  View Project
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm">{format(parseISO(request.created_at), 'MMM d, yyyy h:mm a')}</p>
              </div>
              {request.desired_due_date && (
                <div>
                  <p className="text-xs text-slate-500">Desired Due Date</p>
                  <p className="text-sm">{format(parseISO(request.desired_due_date), 'MMM d, yyyy')}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-500">SLA Due Date</p>
                <p className={`text-sm font-medium ${request.is_sla_breached ? 'text-red-600' : ''}`}>
                  {format(parseISO(request.sla_due_date), 'MMM d, yyyy h:mm a')}
                  {request.sla_hours_remaining && !request.is_sla_breached && (
                    <span className="text-slate-500 font-normal ml-1">
                      ({Math.round(request.sla_hours_remaining)}h remaining)
                    </span>
                  )}
                </p>
              </div>
              {request.completed_at && (
                <div>
                  <p className="text-xs text-slate-500">Completed</p>
                  <p className="text-sm">{format(parseISO(request.completed_at), 'MMM d, yyyy h:mm a')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Requester */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Requester</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-medium">{request.requester_name}</p>
                  <p className="text-xs text-slate-500">{request.business_unit}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status History */}
          <Card className="mcc-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {request.status_history?.slice().reverse().map((entry, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {STATUS_CONFIG[entry.status]?.label || entry.status}
                      </p>
                      <p className="text-xs text-slate-500">
                        {format(parseISO(entry.changed_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailPage;
